/** Mirrors middleware/web-view-context.js for client-side hints sent with chat requests. */

export interface WebViewContext {
  isInAppWebView: boolean;
  webViewKind: string;
  webViewShell: string;
  nativeBridge?: boolean;
}

function classifyUserAgentWebView(userAgent: string): Pick<WebViewContext, 'isInAppWebView' | 'webViewKind'> {
  const ua = userAgent || '';

  if (/\;\s*wv\)/i.test(ua)) {
    return { isInAppWebView: true, webViewKind: 'android-webview' };
  }

  if (/FBAN|FBAV|FB_IAB/i.test(ua)) {
    return { isInAppWebView: true, webViewKind: 'facebook-iab' };
  }

  if (/Instagram/i.test(ua)) {
    return { isInAppWebView: true, webViewKind: 'instagram-iab' };
  }

  if (/\bLine\//i.test(ua)) {
    return { isInAppWebView: true, webViewKind: 'line-iab' };
  }

  const isIos = /iPhone|iPod|iPad/i.test(ua);
  if (isIos) {
    const hasFullBrowser =
      /CriOS|FxiOS|EdgiOS|OPiOS|SamsungBrowser/i.test(ua) ||
      /Version\/[\d.]+.*Safari/i.test(ua);
    if (/Mobile\/[\dA-F]+/i.test(ua) && !hasFullBrowser) {
      return { isInAppWebView: true, webViewKind: 'ios-wkwebview' };
    }
  }

  return { isInAppWebView: false, webViewKind: 'browser' };
}

function detectWebViewShell(userAgent: string, referer: string): string {
  const combined = `${userAgent} ${referer}`.toLowerCase();

  if (/subsplash/.test(combined)) {
    return 'subsplash';
  }
  if (/fbav|fban|facebook/.test(combined)) {
    return 'facebook';
  }
  if (/instagram/.test(combined)) {
    return 'instagram';
  }
  if (/\bline\//.test(combined)) {
    return 'line';
  }

  return 'none';
}

function hasNativeWebViewBridge(): boolean {
  const w = window as Window & { ReactNativeWebView?: unknown };

  // Only treat explicit hybrid-app bridges as in-app WebView. Generic
  // webkit.messageHandlers also exists in standalone Safari and caused false
  // positives that forced the visible Turnstile modal on normal mobile browsers.
  return !!w.ReactNativeWebView;
}

/** Build client context sent on chat requests (merged with server UA classification). */
export function detectWebViewContext(): WebViewContext {
  const ua = navigator.userAgent;
  const referer = document.referrer || '';
  const uaClass = classifyUserAgentWebView(ua);
  const nativeBridge = hasNativeWebViewBridge();

  let isInAppWebView = uaClass.isInAppWebView || nativeBridge;
  let webViewKind = uaClass.webViewKind;
  let webViewShell = detectWebViewShell(ua, referer);

  if (nativeBridge && webViewKind === 'browser') {
    webViewKind = 'ios-wkwebview';
  }

  if (isInAppWebView && webViewShell === 'none') {
    webViewShell = 'unknown';
  }

  if (!isInAppWebView) {
    webViewShell = 'none';
    webViewKind = 'browser';
  }

  return {
    isInAppWebView,
    webViewKind,
    webViewShell,
    nativeBridge: nativeBridge || undefined,
  };
}

/**
 * Cloudflare Turnstile invisible/PAT often fails in WKWebView, iPad Safari, and strict
 * privacy browsers (600010 / silent-challenge-exhausted). Prefer the visible Managed widget first.
 */
export function shouldPreferInteractiveTurnstile(): boolean {
  const webView = detectWebViewContext();
  if (webView.isInAppWebView) {
    return true;
  }

  const ua = navigator.userAgent || '';

  if (/iPad/i.test(ua)) {
    return true;
  }

  // iPadOS 13+ may report as Macintosh with touch points.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return true;
  }

  // Mobile Safari (exclude Chrome/Firefox/Edge wrappers on iOS).
  const isIos = /iPhone|iPod|iPad/i.test(ua);
  if (
    isIos &&
    /Safari/i.test(ua) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium|Edg/i.test(ua)
  ) {
    return true;
  }

  // Desktop Safari — invisible PAT often fails; Jul 9 logs showed repeated 600010/300030.
  if (/Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR|CriOS|FxiOS|EdgiOS/i.test(ua)) {
    return true;
  }

  // Firefox / Fennec (mobile and desktop) — strict tracking protection blocks Turnstile PAT.
  if (/Firefox|Fennec|Gecko\//i.test(ua)) {
    return true;
  }

  // All Android browsers (phone + tablet). Fennec often sends a Chrome-compatible UA
  // without "Firefox", and invisible PAT frequently returns 600010 before interactive.
  if (/Android/i.test(ua)) {
    return true;
  }

  return false;
}
